import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateAttemptInput } from '@proref/schemas';
import type { DailyClip, StreakSummary } from '@proref/types';
import { getReveal, submitAttempt } from '../services/attempts.service';

// The persisted reveal for an already-answered clip. Gated server-side, so only enable it
// when the clip's viewer state says the referee has answered — otherwise the API would 403.
// Keyed separately from the clip so a fresh playback URL fetch doesn't clobber the reveal.
export function useRevealQuery(clipId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['clip-reveal', clipId],
    queryFn: () => getReveal(clipId as string),
    enabled: Boolean(clipId) && enabled,
    // A reveal is immutable once the clip is answered — never restale it within the session.
    staleTime: Infinity,
  });
}

// Submit an answer. On success the clip's viewer state + counters have changed, so refresh
// the single-clip query (a remount should read as answered) and the referee library (the
// clip's attempt counters). The reveal itself comes back from the mutation, so the caller
// renders it directly rather than waiting on a refetch.
//
// The Home hero + streak card read separate queries (['daily-clip'] / ['streak']) that the
// answer also affects. We patch those in cache IMMEDIATELY so navigating Home shows the answered
// state with no lag — otherwise Home paints its stale "not answered" cache first and only corrects
// on a background refetch (the split-second flicker). Invalidation still runs so the authoritative
// numbers (exact streak count) reconcile silently.
export function useSubmitAttempt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAttemptInput) => submitAttempt(input),
    onSuccess: (reveal, vars) => {
      void qc.invalidateQueries({ queryKey: ['clip', vars.clip_id] });
      void qc.invalidateQueries({ queryKey: ['clip-reveal', vars.clip_id] });
      void qc.invalidateQueries({ queryKey: ['referee-clips'] });

      // Was the clip just answered today's assigned (daily) clip? Only then do the Home hero's
      // "Done" state + the streak advance. Read the cached daily clip to compare ids.
      const daily = qc.getQueryData<DailyClip>(['daily-clip']);
      const isDailyClip = daily?.clip?.id === vars.clip_id;
      if (isDailyClip && daily && !daily.answered) {
        // Flip the hero to answered instantly, and reconcile in the background.
        qc.setQueryData<DailyClip>(['daily-clip'], { ...daily, answered: true });
        void qc.invalidateQueries({ queryKey: ['daily-clip'] });
      }

      // Only a FIRST attempt moves the referee's stats (mastery/accuracy/clips-answered) and the
      // streak; a practice repeat is recorded but doesn't score, so skip those for repeats.
      if (reveal.is_first) {
        void qc.invalidateQueries({ queryKey: ['referee-stats'] });
        // Per-rule accuracy is a weighted recompute server-side (attempts_count-weighted), not a
        // simple +1 — plain invalidate + background refetch rather than an optimistic patch, same
        // reasoning as referee-stats above. RuleMasteryCard animates the radar/percentages itself.
        void qc.invalidateQueries({ queryKey: ['referee-mastery'] });
        // A first answer on the daily clip is what advances the streak — mark today done and bump
        // the count optimistically so the streak card doesn't lag, then invalidate to confirm.
        if (isDailyClip) {
          qc.setQueryData<StreakSummary>(['streak'], (prev) =>
            prev && !prev.answered_today
              // Spread `prev` rather than rebuilding the object: the deadline (`resets_at`) is
              // unchanged by answering — the day still ends when it ends — and dropping it here
              // would blank the countdown until the invalidate below came back.
              ? { ...prev, answered_today: true, current_streak: prev.current_streak + 1 }
              : prev,
          );
          void qc.invalidateQueries({ queryKey: ['streak'] });
        }
      }
    },
  });
}
